import {
    Breadcrumb,
    DataTable,
    DateField,
    EditButton,
    NumberField,
    ReferenceField,
    ReferenceManyField,
    Show,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, ClipboardList, FileText, User, Wrench } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';

const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/design_requests">Design Requests</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>{record.reference}</Breadcrumb.PageItem>
            </Breadcrumb>
            <span className="text-xl font-bold tracking-tight text-foreground">{record.reference}</span>
        </>
    );
};
import { StatusBadge, StatusBadgeField } from '../documents/shared/StatusBadge';
import { DesignRequest } from '../types';
import { CreateDesignBoMButton } from './CreateDesignBoMButton';
import { DesignApprovalWorkflow } from './DesignApprovalWorkflow';
import { DesignRevisionComments } from './DesignRevisionComments';
import { DesignVersionHistory } from './DesignVersionHistory';

const DesignRequestShowActions = () => (
    <div className="flex items-center gap-2">
        <CreateDesignBoMButton />
        <EditButton />
    </div>
);

const DesignRequestShowContent = () => {
    const record = useRecordContext<DesignRequest>();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            {/* Header Section */}
            <div className="flex justify-between items-start">
                <div className="flex gap-6 items-center">
                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center border-2 border-blue-100 shadow-sm">
                        <Wrench className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <h2 className="text-3xl font-bold tracking-tight text-foreground">{record.reference}</h2>
                            <StatusBadge status={record.status} />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                             <div className="font-semibold text-foreground">{record.title}</div>
                             <span className="text-slate-300">|</span>
                             <ReferenceField source="project_id" reference="projects" link="show">
                                 <TextField source="name" className="text-primary hover:underline font-medium" />
                             </ReferenceField>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-3 mb-8">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="specifications">Specifications</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-2 space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                        <FileText className="w-4 h-4" /> Description
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="text-foreground leading-relaxed font-medium">
                                    {record.description || "No description provided."}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                        <ClipboardList className="w-4 h-4" /> Technical Scope
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <span className="text-xs font-bold text-muted-foreground uppercase">Priority</span>
                                            <div>
                                                <Badge variant="outline" className="font-bold border-blue-200 text-blue-700 bg-blue-50 uppercase">
                                                    {record.priority}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-xs font-bold text-muted-foreground uppercase">Due Date</span>
                                            <div className="flex items-center gap-2 font-semibold">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                <DateField source="due_date" />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="space-y-6">
                            <Card className="bg-muted border-none shadow-none">
                                <CardHeader>
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Assignment</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-card rounded-full flex items-center justify-center border shadow-sm">
                                            <User className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                        <div className="space-y-0.5">
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Designer</span>
                                            <div className="text-sm font-bold text-foreground">
                                                <ReferenceField source="assigned_designer_id" reference="sales" link="show">
                                                    <TextField source="first_name" />
                                                </ReferenceField>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-4 border-t border-border">
                                        <div className="text-[10px] font-bold text-muted-foreground uppercase mb-2">Requested On</div>
                                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                            <Calendar className="w-4 h-4" />
                                            <DateField source="requested_at" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <DesignApprovalWorkflow />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="specifications">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader className="pb-3 border-b">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                    <Wrench className="w-3.5 h-3.5" /> Design Parameters
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6">
                                {record.specifications_jsonb ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {Object.entries(typeof record.specifications_jsonb === 'string' ? JSON.parse(record.specifications_jsonb as string) : record.specifications_jsonb).map(([key, value]) => (
                                            <div key={key} className="flex items-center justify-between py-3 border-b border-dashed border-border">
                                                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">{key.replace(/_/g, ' ')}</span>
                                                <span className="text-sm font-semibold text-foreground">{String(value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-8 text-center text-muted-foreground text-sm">
                                        No specifications have been added yet. Use the Edit button to add design parameters.
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3 border-b bg-muted/50">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                    <ClipboardList className="w-3.5 h-3.5" /> Design Checklist
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="space-y-3">
                                    {['Materials selected', 'Dimensions confirmed', 'Cost estimated', 'Client approval obtained', 'BoM generated'].map((item) => (
                                        <label key={item} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted transition-colors cursor-default">
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${record.status === 'approved' ? 'bg-emerald-100 border-emerald-400 text-emerald-600' : 'border-input text-transparent'}`}>
                                                ✓
                                            </div>
                                            <span className="text-sm text-foreground font-medium">{item}</span>
                                        </label>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
                
                <TabsContent value="history">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader className="pb-3 border-b">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                    <FileText className="w-3.5 h-3.5" /> Linked Bills of Materials
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ReferenceManyField reference="boms" target="design_request_id">
                                    <DataTable bulkActionButtons={false} rowClick="show">
                                        <DataTable.Col source="reference" label="BoM Ref" className="font-mono font-bold text-blue-600" />
                                        <DataTable.Col source="version" label="Version" />
                                        <DataTable.Col source="status" label="Status">
                                            <StatusBadgeField source="status" />
                                        </DataTable.Col>
                                        <DataTable.Col source="total_estimated_cost" label="Est. Cost">
                                            <NumberField source="total_estimated_cost" options={{ style: 'currency', currency: 'INR' }} />
                                        </DataTable.Col>
                                    </DataTable>
                                </ReferenceManyField>
                            </CardContent>
                        </Card>

                        <DesignVersionHistory />
                        
                        <DesignRevisionComments />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export const DesignRequestShow = () => (
    <Show title={<ShowTitle />} actions={<DesignRequestShowActions />} disableBreadcrumb>
        <DesignRequestShowContent />
    </Show>
);
